import React, { CSSProperties } from "react";

import range from "lodash/range";

import styles from "../../assets/stylesheets/menu/three-dot-loader.module.scss";
import mainStyles from "./main.module.scss";

const defaultObj = {};

export type SpinnerLoaderProps = {
  width?: number;
  height?: number;
  viewBox?: string;
  stroke?: string;
  strokeWidth?: string;
  className?: string;
};

export const Spinner: React.FC<SpinnerLoaderProps> = ({
  width = 46,
  height = 46,
  viewBox = "-1 -1 46 46",
  stroke = "#2D2D2D",
  strokeWidth = "3",
  className = "",
}) => (
  <svg
    width={width}
    height={height}
    viewBox={viewBox}
    xmlns="http://www.w3.org/2000/svg"
    stroke={stroke}
    className={`oval ${className}`}
  >
    <g fill="none" fillRule="evenodd">
      <g transform="translate(1 1)" strokeWidth={strokeWidth}>
        <circle strokeOpacity=".2" cx="18" cy="18" r="18" />
        <path d="M36 18c0-9.94-8.06-18-18-18">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 18 18"
            to="360 18 18"
            dur="1s"
            repeatCount="indefinite"
          />
        </path>
      </g>
    </g>
  </svg>
);

export type ButtonLoaderProps = {
  stroke?: string;
  fillStroke?: string;
  wrapperClass?: string;
  containerClass?: string;
  loaderWrapperPrefixClass?: string;
  containerPrefixClass?: string;
};
export const ButtonLoader: React.FC<ButtonLoaderProps> = ({
  stroke = "#AEB6BF",
  fillStroke = "#fff",
  wrapperClass = "",
  containerClass = "",
  loaderWrapperPrefixClass = "loader-wrapper",
  containerPrefixClass = "button-loader",
}) => (
  <div className={`${loaderWrapperPrefixClass} ${wrapperClass}`}>
    <div className={`${containerPrefixClass} ${containerClass}`}>
      <svg
        version="1.1"
        id="L4"
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        viewBox="0 0 115 16"
        enableBackground="new 0 0 0 0"
        stroke={stroke}
      >
        <circle fill={fillStroke} stroke="none" cx="6" cy="10" r="5">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite"
            begin="0.1"
          />
        </circle>
        <circle fill={fillStroke} stroke="none" cx="26" cy="10" r="5">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite"
            begin="0.2"
          />
        </circle>
        <circle fill={fillStroke} stroke="none" cx="46" cy="10" r="5">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite"
            begin="0.3"
          />
        </circle>
        <circle fill={fillStroke} stroke="none" cx="66" cy="10" r="5">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite"
            begin="0.4"
          />
        </circle>
        <circle fill={fillStroke} stroke="none" cx="86" cy="10" r="5">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite"
            begin="0.5"
          />
        </circle>
        <circle fill={fillStroke} stroke="none" cx="106" cy="10" r="5">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite"
            begin="0.6"
          />
        </circle>
      </svg>
    </div>
  </div>
);

export const BSpinner = ({ className = "" }) => (
  <div className={`spinner-border ${className}`} role="status">
    <span className="sr-only">Loading...</span>
  </div>
);

export const Dot = ({ className = "" }) => (
  <div className={`spinner-grow ${className}`} role="status">
    <span className="sr-only">Loading...</span>
  </div>
);

export const ThreeDots = ({ className = "", dotClass = "" }) => (
  <div className={className}>
    {[...Array(3)].map((_, idx) => (
      <Dot key={`dot-${idx}`} className={`${dotClass} mr-2`} />
    ))}
  </div>
);

export const PageLoader = ({
  wrapperClass = "",
  style = defaultObj,
}: {
  style?: CSSProperties;
  wrapperClass?: string;
}) => (
  <div
    style={style}
    className={`d-flex align-items-center justify-content-center min-h-100vh ${wrapperClass}`}
  >
    <Spinner />
  </div>
);

export const ThreeProgressiveDots = ({ className = "", dotClass = "" }) => (
  <div className={`${className} loader-wrapper`}>
    {[...Array(3)].map((_, idx) => (
      <div key={`dot-${idx}`} className={`${dotClass} dot dot${idx + 1} `} />
    ))}
  </div>
);

export type RoundButtonLoaderProps = object;
export const RoundButtonLoader: React.FC<RoundButtonLoaderProps> = () => (
  <span className="spinner-border text-grey-e2 align-middle mx-1 mx-2xl-1 loader-dark" />
);

export type ThreeDotLoaderProps = {
  className?: string;
};
export const ThreeDotLoader: React.FC<ThreeDotLoaderProps> = ({ className = "" } = {}) => (
  <span className={`bg-white  text-center ${className} ${styles.threeDotLoader}`}>
    {range(3).map((num) => (
      <span key={`dot-${num}`} className={`${styles.loaderDot}`}>
        &bull;
      </span>
    ))}
  </span>
);

export type ContentLoaderProps = {
  className?: string;
  baseClass?: string;
};

export const ContentLoader: React.FC<ContentLoaderProps> = ({
  className = "",
  baseClass = `animated-background ${mainStyles.animatedBackground}`,
}) => <div className={`${baseClass} ${className}`} />;

export const HeaderLoader: React.FC<ContentLoaderProps> = ({
  className = "",
  baseClass = mainStyles.headerLoader,
}) => (
  <div className={`${baseClass} ${className}`}>
    <div className={mainStyles.headerImageBlock}>
      <div className={mainStyles.loader}>
        <svg
          width="436"
          height="445"
          viewBox="0 0 436 445"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M251.461 0.552551V140.437L218.122 174.452L184.846 140.437V0.552551H251.461Z"
            fill="#8D12E7"
          />
          <path
            d="M251.461 236.532V444.448H184.846V304.501L218.122 270.548L231.937 256.453L251.461 236.532Z"
            fill="#00C036"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M204.369 188.484L218.122 174.452L87.9001 41.5214L40.8091 89.5694L171.031 222.5L184.845 208.405L204.369 188.484ZM232.225 256.516L218.472 270.548L348.694 403.479L395.785 355.431L265.563 222.5L251.749 236.595L232.225 256.516Z"
            fill="#0B7AFC"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M185.134 304.206V304.564L232.225 256.516L251.749 236.595L265.563 222.5L395.785 89.6325L348.694 41.5845L251.46 140.794V140.436L204.369 188.484L184.845 208.405L171.031 222.5L40.8093 355.368L87.9003 403.416L185.134 304.206Z"
            fill="#FF5B77"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M137.753 256.453L171.03 222.5L137.753 188.485H0.59375V256.453H137.753ZM298.841 188.547L265.564 222.5L298.841 256.516L436 256.516V188.547L298.841 188.547Z"
            fill="#FFB800"
          />
        </svg>
      </div>
    </div>
  </div>
);

export const BoxLoader: React.FC<ContentLoaderProps> = ({ className, baseClass = "" }) => (
  <div
    className={`animated-background ${mainStyles.animatedBackground} ${mainStyles.boxLoader} ${baseClass} ${className}`}
  ></div>
);

export default {
  Spinner,
  BSpinner,
  Dot,
  ThreeDots,
  PageLoader,
  ThreeProgressiveDots,
  RoundButtonLoader,
  ThreeDotLoader,
  HeaderLoader,
  BoxLoader,
};
